import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useState, useEffect } from "react";
import {
  useGetFacultyAndAdminsQuery,
  useAssignCourseToFacultyMutation,
  useRemoveCourseAssignmentFromFacultyMutation,
  useRevokeFacultyMutation,
  useInviteUserToSchoolMutation,
  GetFacultyAndAdminsDocument,
} from "../../graphql/generated/graphql";
import AdminLayout from "../../layouts/AdminLayout.web";
import { MotiView } from "moti";
import { useAppConfig } from "../../AppConfigProvider";
import AdminTable from "../../components/adminDashboard/AdminTable/AdminTable";
import AdminTableHeader from "../../components/adminDashboard/AdminTable/AdminTableHeader";
import AdminTablePagination from "../../components/adminDashboard/AdminTable/AdminTablePagination";
import AdminUsersTable, { AdminUsersModalState } from "../../components/adminDashboard/AdminUsersTable";
import { PlusCircle } from "../../components/svgIcons";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import usePagination from "../../hooks/usePagination";
import useSortingDropdown from "../../hooks/useSortingDropdown";
import NewCustomAxioActionModal from "../../containers/settings/UsersTabContainer/NewCustomAxioActionModal";
import AssignCourseToFacultyListContainer from "../../containers/settings/UsersTabContainer/AssignCourseToFacultyListContainer";
import RemoveCourseFromFacultyListContainer from "../../containers/settings/UsersTabContainer/RemoveCourseFromFacultyList";
import InviteFacultyModalContainer from "../../containers/settings/UsersTabContainer/InviteFacultyModalContainer";

const SORT_ITEMS = [
  { label: "Name (A-Z)", value: "name_asc" },
  { label: "Name (Z-A)", value: "name_desc" },
  { label: "Date (Newest First)", value: "date_desc" },
  { label: "Date (Oldest First)", value: "date_asc" },
];

const modals = {
  revokeFaculty: {
    title: "Remove Teacher from Platform?",
    ariaLabel: "Remove Teacher Modal",
    warningText:
      "Following faculty will no longer have access to the platform. This action cannot be undone, and all associated data will be deleted.",
    actionButtonText: "Remove Teacher",
  },
  revokeConfirmation: {
    title: "Success!",
    ariaLabel: "Revoke Confirmation Modal",
    warningText: "The faculty has been successfully revoked.",
    actionButtonText: "OK",
  },
};

type AdminUser = {
  id: string;
  name: string;
  email: string;
  lastActive: string;
  hasRoleRoles: { name: string }[];
  teachingCourses: { name: string; course_code?: string | null }[];
  pendingFacultyInvite: boolean;
  expiredInvitation: boolean;
  hasSessionDeviceSessions: { id: string }[];
};

function AdminUsersView() {
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const [modalState, setModalState] = useState({
    state: AdminUsersModalState.CLOSED,
    userId: "",
    userName: "",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 5 });

  const {
    data: usersData,
    refetch: usersRefetch,
    loading: usersLoading,
  } = useGetFacultyAndAdminsQuery({
    variables: {
      schoolId,
      page,
      pageSize,
      searchQuery,
      sortBy,
    },
    skip: !schoolId,
  });

  const users = usersData?.getFacultyAndAdmins?.data.faculties || [];
  const totalCount = usersData?.getFacultyAndAdmins?.data.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const refetchQueries = [
    {
      query: GetFacultyAndAdminsDocument,
      variables: {
        schoolId,
        page,
        pageSize,
        searchQuery,
        sortBy,
      },
    },
  ];

  const [assignCourseToFaculty] = useAssignCourseToFacultyMutation();
  const [removeCourseFromFaculty] = useRemoveCourseAssignmentFromFacultyMutation();
  const [inviteUserToSchool] = useInviteUserToSchoolMutation();
  const [revokeFaculty] = useRevokeFacultyMutation();

  const inviteMember = async ({
    email,
    name,
    role,
  }: {
    email: string;
    name: string;
    role: "School Admin" | "Faculty" | "Auditor";
  }) => {
    setIsSubmitting(true);
    try {
      const result = await inviteUserToSchool({
        variables: {
          input: {
            email,
            name,
            role,
          },
        },
        refetchQueries: refetchQueries,
      });
      setIsSubmitting(false);
      return result.data?.inviteUserToSchool?.success;
    } catch (error) {
      setIsSubmitting(false);
      return false;
    }
  };

  const assignCourse = async (courseId: string) => {
    await assignCourseToFaculty({
      variables: {
        userId: modalState.userId,
        courseId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: AdminUsersModalState.CLOSED, userId: "", userName: "" });
  };

  const removeCourse = async (courseId: string) => {
    await removeCourseFromFaculty({
      variables: {
        userId: modalState.userId,
        courseId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: AdminUsersModalState.CLOSED, userId: "", userName: "" });
  };

  const handleRevokeFaculty = async () => {
    await revokeFaculty({
      variables: {
        userId: modalState.userId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: AdminUsersModalState.REVOKE_CONFIRMATION, userId: "", userName: "" });
    setSearchQuery("");
  };

  const handleActionModalOpen = (userId: string, userName: string, modalType: AdminUsersModalState) => {
    setModalState({ state: modalType, userId, userName });
  };

  const handleModalClose = () => {
    setModalState({ state: AdminUsersModalState.CLOSED, userId: "", userName: "" });
  };

  // Transform GraphQL data to match component interface
  const transformedUsers: AdminUser[] = users.map((user) => ({
    id: user.id || "",
    name: user.name || "",
    email: user.email || "",
    lastActive: "",
    hasRoleRoles: (user.hasRoleRoles || []).map((role) => ({
      name: role.name || "",
    })),
    teachingCourses: (user.teachingCourses || []).map((course) => ({
      name: course.name || "",
      course_code: course.course_code || null,
    })),
    pendingFacultyInvite: user.pendingFacultyInvite || false,
    expiredInvitation: user.expiredInvitation || false,
    hasSessionDeviceSessions: (user.hasSessionDeviceSessions || []).map((session) => ({
      id: session.id || "",
    })),
  }));

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    usersRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{
              opacity: 0,
              translateY: 20,
              scale: 1,
            }}
            animate={{
              opacity: 1,
              translateY: 0,
              scale: 1,
            }}
            delay={10}
            // @ts-expect-error - transition is not typed
            transition={{ type: "timing", duration: 500 }}>
            <View>
              <CustomText
                text="Users"
                size="m"
                useTranslationText={false}
                textType="display"
                weight="ultraBold"
                role="heading"
                aria-level="1"
                style={{ color: "#101828" }}
              />

              <CustomSpacing type="vertical" size="s" />

              <CustomText
                text="Manage admin users, faculty, and their course assignments."
                size="m"
                weight="medium"
                useTranslationText={false}
                style={{ color: "#667085" }}
              />
              <CustomSpacing type="vertical" size="xl" />
              <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                <View>
                  {users.length > 0 && (
                    <>
                      <CustomText
                        style={{ color: "#101828" }}
                        size="l"
                        weight="bold"
                        text={`Total Users (${totalCount})`}
                        useTranslationText={false}
                      />
                    </>
                  )}
                </View>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <CustomButton
                    styleType="primary"
                    bold={true}
                    text="Invite"
                    size="m"
                    leftIcon={<PlusCircle color={"#FFFFFF"} />}
                    onPress={() => setModalState({ state: AdminUsersModalState.INVITE, userId: "", userName: "" })}
                  />
                </View>
              </View>
              <CustomSpacing type="vertical" size="xl" />
            </View>
            <AdminTable>
              <AdminTableHeader
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDropdownOpen={sortDropdownOpen}
                setSortDropdownOpen={setSortDropdownOpen}
                sortItems={SORT_ITEMS}
              />

              {usersLoading ? (
                <View style={styles.loaderContainer}>
                  <ActivityIndicator size="large" color="#0084FF" />
                </View>
              ) : (
                <AdminUsersTable
                  users={transformedUsers}
                  onActionModalOpen={handleActionModalOpen}
                  resendInvite={inviteMember}
                />
              )}
              {totalCount > 0 && (
                <AdminTablePagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={setPage}
                  onItemsPerPageChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setPage(1);
                  }}
                />
              )}
            </AdminTable>
          </MotiView>

          {/* Modals */}
          <NewCustomAxioActionModal
            visible={modalState.state === AdminUsersModalState.REVOKE_FACULTY}
            onClose={handleModalClose}
            onAction={handleRevokeFaculty}
            modalTexts={modals.revokeFaculty}
            contextualText={modalState.userName}
          />
          <NewCustomAxioActionModal
            visible={modalState.state === AdminUsersModalState.REVOKE_CONFIRMATION}
            onClose={handleModalClose}
            modalTexts={modals.revokeConfirmation}
            contextualText={modalState.userName}
          />
          <AssignCourseToFacultyListContainer
            visible={modalState.state === AdminUsersModalState.ASSIGN_COURSE}
            onClose={handleModalClose}
            onAction={assignCourse}
          />
          <RemoveCourseFromFacultyListContainer
            visible={modalState.state === AdminUsersModalState.REMOVE_COURSE}
            onClose={handleModalClose}
            onAction={removeCourse}
            userId={modalState.userId}
          />
          <InviteFacultyModalContainer
            visible={modalState.state === AdminUsersModalState.INVITE}
            onClose={handleModalClose}
            onSubmit={inviteMember}
            isSubmitting={isSubmitting}
          />
        </View>
      </View>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
    minHeight: "100vh",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
});

export default AdminUsersView;
